import React, { useState } from 'react'
import { PaddingType } from 'common/types/styleTypes'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { ExpandButton } from 'src/editor/shared/ui-lib/expand-button'
import { NumberInput } from 'src/editor/shared/ui-lib/input/number-input'
import { MobileIcon } from 'src/editor/shared/ui-lib/mobile-icon'
import { PaddingBottomIcon } from './icons/padding-bottom-icon'
import { PaddingTopIcon } from './icons/padding-top-icon'
import { PaddingLeftIcon } from './icons/padding-left-icon'
import { PaddingRightIcon } from './icons/padding-right-icon'
import { PaddingLeftRightIcon } from './icons/padding-left-right-icon'
import { PaddingTopBottomIcon } from './icons/padding-top-bottom-icon'

interface PaddingStyleProps {
  padding: PaddingType | undefined
  update: (padding: PaddingType) => void
  label?: string
  allowNegative?: boolean
  withoutExpandButton?: boolean
}

type SideType =
  | 'all'
  | 'top-bottom'
  | 'left-right'
  | 'paddingLeft'
  | 'paddingRight'
  | 'paddingTop'
  | 'paddingBottom'

export const PaddingGroup = ({
  padding = {
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
  },
  update,
  label = 'settings_styles.padding.label',
  withoutExpandButton = false,
}: PaddingStyleProps) => {
  const { isMobile } = useDeviceModeContext()
  const getIsVisibleFullSettings = (): boolean => {
    return !(
      padding.paddingTop === padding.paddingBottom &&
      padding.paddingLeft === padding.paddingRight
    )
  }
  const [isVisibleFullSetting, setIsVisibleFullSetting] = useState(
    getIsVisibleFullSettings,
  )

  const switchSettings = () => {
    if (isVisibleFullSetting) {
      handleUpdate()()
    }
    setIsVisibleFullSetting(!isVisibleFullSetting)
  }

  const handleUpdate =
    (type: SideType = 'all') =>
    (value = 0) => {
      switch (type) {
        case 'top-bottom':
          return update({
            ...padding,
            paddingTop: +value,
            paddingBottom: +value,
          })
        case 'left-right':
          return update({
            ...padding,
            paddingLeft: +value,
            paddingRight: +value,
          })
        case 'paddingBottom':
        case 'paddingLeft':
        case 'paddingRight':
        case 'paddingTop':
          return update({
            ...padding,
            [type]: +value,
          })
        default:
          return update({
            paddingTop: +(padding.paddingTop ?? padding.paddingBottom ?? 0),
            paddingBottom: +(padding.paddingTop ?? padding.paddingBottom ?? 0),

            paddingRight: +(padding.paddingLeft ?? padding.paddingRight ?? 0),
            paddingLeft: +(padding.paddingLeft ?? padding.paddingRight ?? 0),
          })
      }
    }

  return (
    <>
      <OptionWrapper
        labelText={label}
        labelIcon={isMobile ? <MobileIcon /> : null}
      >
        <div className="flex gap-[6px]">
          <div className="grid grow-1 grid-cols-2 gap-3">
            {!isVisibleFullSetting ? (
              <>
                <NumberInput
                  startElement={<PaddingTopBottomIcon />}
                  onChange={handleUpdate('top-bottom')}
                  value={padding.paddingTop ?? padding.paddingBottom ?? 0}
                />
                <NumberInput
                  startElement={<PaddingLeftRightIcon />}
                  onChange={handleUpdate('left-right')}
                  value={padding.paddingLeft ?? padding.paddingRight ?? 0}
                />
              </>
            ) : (
              <>
                <NumberInput
                  startElement={<PaddingTopIcon />}
                  onChange={handleUpdate('paddingTop')}
                  value={padding.paddingTop ?? 0}
                />
                <NumberInput
                  startElement={<PaddingLeftIcon />}
                  onChange={handleUpdate('paddingLeft')}
                  value={padding.paddingLeft ?? 0}
                />
                <NumberInput
                  startElement={<PaddingBottomIcon />}
                  onChange={handleUpdate('paddingBottom')}
                  value={padding.paddingBottom ?? 0}
                />
                <NumberInput
                  startElement={<PaddingRightIcon />}
                  onChange={handleUpdate('paddingRight')}
                  value={padding.paddingRight ?? 0}
                />
              </>
            )}
          </div>
          {!withoutExpandButton && (
            <ExpandButton
              className="relative top-[5.5px]"
              isActive={isVisibleFullSetting}
              onClick={switchSettings}
            />
          )}
        </div>
      </OptionWrapper>
    </>
  )
}
